import { useRef, useState } from 'react';

import { useCentralizedMe } from '@crehana/auth';
import type { TFileUploadEntry } from '@crehana/compass.web/file-upload';

import {
  AudienceType,
  type NotificationHubTemplateAttachmentInput,
  ProductType,
} from '@/__generated__/graphql/types';

import { templateBlockToInput } from '../components/vista-previa/use-send-test-template-email/template-block-to-input';
import type { TEmailEditorBlock } from '../mail-canvas/blocks/block-types';
import type { TTemplateRecipientType, TTemplateWizardConfig } from '../types';
import { usePlantillasCreateEmailTemplateMutation } from './graphql/create-email-template.v5.generated';

const AUDIENCE_TYPE_FROM_RECIPIENT_TYPE: Record<
  TTemplateRecipientType,
  AudienceType
> = {
  COLLABORATOR: AudienceType.Collaborator,
  LEADER: AudienceType.Leader,
};

const PRODUCT_TYPES = new Set<string>(Object.values(ProductType));

export const isProductType = (value: string): value is ProductType =>
  PRODUCT_TYPES.has(value);

const toAttachmentInput = (
  entries: TFileUploadEntry[],
): NotificationHubTemplateAttachmentInput[] =>
  entries.flatMap(entry =>
    entry.status === 'success' && entry.uploadedFile
      ? [
          {
            file_id: entry.uploadedFile.id,
            name: entry.uploadedFile.name,
            size: entry.uploadedFile.size,
            content_type: entry.uploadedFile.content_type,
          },
        ]
      : [],
  );

type TCreateTemplateInput = {
  config: TTemplateWizardConfig;
  blocks: TEmailEditorBlock[];
  attachments: TFileUploadEntry[];
  productType: ProductType;
  resolveMetricLabel: (metricId: string) => string;
};

type TCreatedTemplate = { id: string };
type TCreateTemplateFailure = { message?: string };
type TCreateTemplateResult = TCreatedTemplate | TCreateTemplateFailure;

export const useCreateTemplate = () => {
  const { currentPageOrganization } = useCentralizedMe();
  const [mutate] = usePlantillasCreateEmailTemplateMutation({
    context: { clientName: 'v5' },
  });

  const [isCreating, setIsCreating] = useState(false);
  const isCreatingRef = useRef(false);

  const create = async ({
    config,
    blocks,
    attachments,
    productType,
    resolveMetricLabel,
  }: TCreateTemplateInput): Promise<TCreateTemplateResult> => {
    if (isCreatingRef.current) {
      return {};
    }

    if (!config.recipientType) {
      return {};
    }

    isCreatingRef.current = true;
    setIsCreating(true);

    try {
      const { data } = await mutate({
        variables: {
          input: {
            centralized_organization_id: currentPageOrganization.id,
            product_type: productType,
            audience_type:
              AUDIENCE_TYPE_FROM_RECIPIENT_TYPE[config.recipientType],
            name: config.name,
            subject: config.subject,
            description: config.description || undefined,
            banner_file_id: config.bannerFileId,
            blocks: blocks.flatMap(block => {
              const input = templateBlockToInput(block, resolveMetricLabel);

              return input ? [input] : [];
            }),
            attachments: toAttachmentInput(attachments),
            custom_variables: [],
          },
        },
      });

      const result = data?.notification_hub.templates.create_email_template;

      if (
        !result ||
        result.__typename !== 'NotificationHubCreateEmailTemplatePayload'
      ) {
        return {
          message: result && 'message' in result ? result.message : undefined,
        };
      }

      return { id: result.template.id };
    } catch (caught) {
      return { message: caught instanceof Error ? caught.message : undefined };
    } finally {
      isCreatingRef.current = false;
      setIsCreating(false);
    }
  };

  return { create, isCreating };
};
